import type {
  AgentPresence,
  AlarmSignalInfo,
  CallObserver,
  CallSnapshotAnsweredEntry,
  CallSnapshotPayload,
  CallTakenBy,
  CallTakenNotice,
  CallTakenPayload,
  ConferenceInvitePayload,
  IncomingCallInfo,
  ObserverJoinedPayload,
  ObserverLeftPayload,
  QueueClaimStatus,
  SipCallState,
  SipRegistrationStatus,
  SuperviseMode,
} from "~/services/calling/types";
import { matchesCallEndedUniqueId } from "~/context/call/helpers";
import { mapSnapshotRingingToQueueableCall } from "~/services/queueCallService";
import type { QueueableCall } from "~/services/queueCallService";
import { mapSnapshotRingingToIncomingCall } from "~/services/calling/adapters";

export type SocketStatus = "disconnected" | "connecting" | "connected" | "error";

export type SecurityEventRecord = {
  id: string;
  eventName: string;
  message: string;
  atMs: number;
};

export type AgentJoinedEventRecord = {
  id: string;
  eventName: string;
  agentId?: string;
  agentName?: string;
  atMs: number;
};

export type AnsweredCallEntry = {
  uniqueId: string;
  caller?: string | null;
  callType?: string | null;
  deviceCid?: string | null;
  startedAt?: string | null;
  answeredAt?: string | null;
  takenBy?: CallTakenBy | null;
  isMine?: boolean;
  observers: CallObserver[];
};

export type BargePromotionNotice = {
  uniqueId: string;
  promotedFromExtension: string;
  atMs: number;
};

export type PendingBargeTransfer = {
  uniqueId: string;
  invitedExtension: string;
  atMs: number;
};

export type BargeTransferNotice = {
  uniqueId: string;
  transferredToName: string;
  transferredToExtension: string;
  atMs: number;
};

export const PENDING_BARGE_TRANSFER_WINDOW_MS = 15_000;

export type CallState = {
  sipStatus: SipRegistrationStatus;
  socketStatus: SocketStatus;
  callStatus: SipCallState;
  callDirection?: "incoming" | "outgoing";
  isMuted: boolean;
  isOnHold: boolean;
  customerId?: string | null;
  callUuid?: string | null;
  callId?: string | null;
  callReference?: string | null;
  unitId?: string | null;
  callerNumber?: string | null;
  customerName?: string | null;
  callStartedAt?: number | null;
  callElapsedSeconds: number;
  conferenceRoom?: string | null;
  incomingCalls: IncomingCallInfo[];
  alarmSignals: AlarmSignalInfo[];
  agents: AgentPresence[];
  participants: AgentPresence[];
  pendingInvite?: ConferenceInvitePayload | null;
  securityEvents: SecurityEventRecord[];
  agentJoinedEvents: AgentJoinedEventRecord[];
  backendHoldLockoutActive: boolean;
  holdLockoutMessage?: string | null;
  lastError?: string | null;
  lastSocketDisconnectReason?: string | null;
  lastSipDisconnectReason?: string | null;
  sipDomain?: string | null;
  callTakenNotices: CallTakenNotice[];
  answeredCalls: AnsweredCallEntry[];
  activeSuperviseMode: SuperviseMode | null;
  bargePromotionNotice: BargePromotionNotice | null;
  pendingBargeTransfer: PendingBargeTransfer | null;
  bargeTransferNotice: BargeTransferNotice | null;
  /** Inbound alarm leg parked on hold while an outbound call is foreground. */
  backgroundHeldCallUuid: string | null;
  backgroundHeldCallReference: string | null;
  /** Remote party on the SIP-held background leg (alarm or consult after swap). */
  heldCallerNumber: string | null;
  /** Outbound consult target while a held alarm leg exists. */
  consultNumber: string | null;
  consultStatus: "idle" | "dialing" | "active" | null;
  queueableCalls: QueueableCall[];
  queueClaimByUniqueId: Record<string, QueueClaimStatus>;
};

export const initialState: CallState = {
  sipStatus: "offline",
  socketStatus: "disconnected",
  callStatus: "idle",
  isMuted: false,
  isOnHold: false,
  callElapsedSeconds: 0,
  incomingCalls: [],
  alarmSignals: [],
  agents: [],
  participants: [],
  pendingInvite: null,
  securityEvents: [],
  agentJoinedEvents: [],
  backendHoldLockoutActive: false,
  holdLockoutMessage: null,
  lastError: null,
  lastSocketDisconnectReason: null,
  lastSipDisconnectReason: null,
  sipDomain: null,
  callTakenNotices: [],
  answeredCalls: [],
  activeSuperviseMode: null,
  bargePromotionNotice: null,
  pendingBargeTransfer: null,
  bargeTransferNotice: null,
  backgroundHeldCallUuid: null,
  backgroundHeldCallReference: null,
  heldCallerNumber: null,
  consultNumber: null,
  consultStatus: null,
  queueableCalls: [],
  queueClaimByUniqueId: {},
};

const INCOMING_CALL_FALLBACK_MERGE_WINDOW_MS = 10_000;

function readMetadataIdentity(
  metadata: Record<string, unknown> | null | undefined,
  ...keys: string[]
) {
  if (!metadata) return null;
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string") {
      const normalized = value.trim();
      if (normalized) return normalized;
    }
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return null;
}

function getIncomingCallIdentity(call: IncomingCallInfo) {
  return (
    call.uniqueId?.trim() ||
    call.callUuid?.trim() ||
    readMetadataIdentity(
      call.metadata,
      "callUuid",
      "call_uuid",
      "uniqueId",
      "unique_id"
    ) ||
    call.callId?.trim() ||
    readMetadataIdentity(call.metadata, "callId", "call_id") ||
    null
  );
}

/** Remove queued incoming calls matching any correlator id (SIP lifecycle cleanup). */
export function removeIncomingCallsByCorrelators(
  prev: CallState,
  correlatorIds: string[]
): CallState {
  const idSet = new Set(
    correlatorIds.map((value) => value.trim()).filter(Boolean)
  );
  if (idSet.size === 0) return prev;

  const nextIncoming = prev.incomingCalls.filter((call) => {
    if (idSet.has(call.id)) return false;
    const uniqueId = getIncomingCallIdentity(call);
    if (uniqueId && idSet.has(uniqueId)) return false;
    const callId = call.callId?.trim();
    if (callId && idSet.has(callId)) return false;
    return true;
  });

  if (nextIncoming.length === prev.incomingCalls.length) return prev;

  const hasIncoming = nextIncoming.length > 0;
  return {
    ...prev,
    incomingCalls: nextIncoming,
    ...(prev.callDirection === "incoming" &&
    prev.callStatus === "ringing" &&
    !hasIncoming
      ? { callStatus: "idle" as const, callDirection: undefined }
      : {}),
  };
}

/** Remove a queued incoming call by telephony unique id (idempotent). */
export function removeIncomingCallByUniqueId(
  prev: CallState,
  uniqueId: string
): CallState {
  const normalizedUniqueId = uniqueId.trim();
  if (!normalizedUniqueId) return prev;

  const nextIncoming = prev.incomingCalls.filter((call) => {
    const rowUniqueId = getIncomingCallIdentity(call);
    if (rowUniqueId === normalizedUniqueId) return false;
    const rowCallId = call.callId?.trim();
    if (rowCallId && rowCallId === normalizedUniqueId) return false;
    return true;
  });

  if (nextIncoming.length === prev.incomingCalls.length) return prev;

  const hasIncoming = nextIncoming.length > 0;
  return {
    ...prev,
    incomingCalls: nextIncoming,
    ...(prev.callDirection === "incoming" &&
    prev.callStatus === "ringing" &&
    !hasIncoming
      ? { callStatus: "idle" as const, callDirection: undefined }
      : {}),
  };
}

function shouldFallbackMergeIncomingCall(
  existing: IncomingCallInfo,
  nextCall: IncomingCallInfo
) {
  if (!existing.callerNumber || !nextCall.callerNumber) return false;
  if (existing.callerNumber !== nextCall.callerNumber) return false;
  const timeDelta = Math.abs(existing.receivedAtMs - nextCall.receivedAtMs);
  if (timeDelta > INCOMING_CALL_FALLBACK_MERGE_WINDOW_MS) return false;
  return !getIncomingCallIdentity(existing) || !getIncomingCallIdentity(nextCall);
}

function patchIncomingCall(
  existing: IncomingCallInfo,
  nextCall: IncomingCallInfo
): IncomingCallInfo {
  return {
    ...existing,
    customerId: nextCall.customerId ?? existing.customerId ?? null,
    uniqueId: nextCall.uniqueId ?? existing.uniqueId ?? null,
    callUuid: nextCall.callUuid ?? existing.callUuid ?? null,
    callId: nextCall.callId ?? existing.callId ?? null,
    service: nextCall.service ?? existing.service ?? null,
    unitId: nextCall.unitId ?? existing.unitId ?? null,
    customerName: nextCall.customerName ?? existing.customerName ?? null,
    callerNumber: nextCall.callerNumber ?? existing.callerNumber ?? null,
    description: nextCall.description ?? existing.description ?? null,
    signalType: nextCall.signalType ?? existing.signalType ?? null,
    requiresAnswer: nextCall.requiresAnswer ?? existing.requiresAnswer ?? null,
    metadata:
      existing.metadata || nextCall.metadata
        ? {
            ...(existing.metadata ?? {}),
            ...(nextCall.metadata ?? {}),
          }
        : null,
    deviceCid: nextCall.deviceCid ?? existing.deviceCid ?? null,
    ringGroup: nextCall.ringGroup ?? existing.ringGroup ?? null,
    receivedAtMs: Math.min(existing.receivedAtMs, nextCall.receivedAtMs),
  };
}

function patchQueueableCallWithIncoming(
  existing: QueueableCall,
  nextCall: IncomingCallInfo
): QueueableCall {
  return {
    ...existing,
    customerId: nextCall.customerId ?? existing.customerId ?? null,
    customerName: nextCall.customerName ?? existing.customerName ?? null,
    unitId: nextCall.unitId ?? existing.unitId ?? null,
    uniqueId: nextCall.uniqueId ?? existing.uniqueId,
    callUuid: nextCall.callUuid ?? existing.callUuid ?? null,
    callId: nextCall.callId ?? existing.callId ?? null,
    callerNumber: nextCall.callerNumber ?? existing.callerNumber ?? null,
    service: nextCall.service ?? existing.service ?? null,
    deviceCid: nextCall.deviceCid ?? existing.deviceCid ?? null,
  };
}

function patchQueueableCallsWithIncoming(
  queueableCalls: QueueableCall[],
  nextCall: IncomingCallInfo
): QueueableCall[] {
  const nextIdentity = getIncomingCallIdentity(nextCall);
  if (!nextIdentity) return queueableCalls;

  const index = queueableCalls.findIndex(
    (call) => getQueueableCallUniqueId(call) === nextIdentity
  );
  if (index >= 0) {
    return queueableCalls.map((call, callIndex) =>
      callIndex === index ? patchQueueableCallWithIncoming(call, nextCall) : call
    );
  }

  if (!nextCall.customerId && !nextCall.customerName && !nextCall.unitId) {
    return queueableCalls;
  }

  const queueableCall: QueueableCall = {
    ...nextCall,
    id: nextCall.id ?? nextIdentity,
    uniqueId: nextIdentity,
    callUuid: nextCall.callUuid ?? nextIdentity,
    callId: nextCall.callId ?? nextIdentity,
    callerNumber: nextCall.callerNumber ?? null,
    customerId: nextCall.customerId ?? null,
    customerName: nextCall.customerName ?? null,
    unitId: nextCall.unitId ?? null,
    service: nextCall.service ?? null,
    description: nextCall.description ?? "Voice Call",
    signalType: nextCall.signalType ?? null,
    requiresAnswer: nextCall.requiresAnswer ?? null,
    metadata: nextCall.metadata ?? null,
    receivedAtMs: nextCall.receivedAtMs ?? Date.now(),
  };

  return [queueableCall, ...queueableCalls].slice(0, 25);
}

export function hasEstablishedOperatorSession(
  state: Pick<CallState, "callStatus" | "callDirection">
): boolean {
  return (
    state.callStatus === "active" ||
    state.callStatus === "held" ||
    (state.callStatus === "dialing" && state.callDirection === "outgoing")
  );
}

/** Single place for "add incoming call" state update. Used by SIP, socket, and operator WS. */
export function applyIncomingCall(
  prev: CallState,
  nextCall: IncomingCallInfo
): CallState {
  const nextIdentity = getIncomingCallIdentity(nextCall);
  const existingIndex = prev.incomingCalls.findIndex((call) => {
    const existingIdentity = getIncomingCallIdentity(call);
    if (nextIdentity && existingIdentity === nextIdentity) return true;
    if (call.id === nextCall.id) return true;
    return shouldFallbackMergeIncomingCall(call, nextCall);
  });
  const incomingCalls =
    existingIndex >= 0
      ? prev.incomingCalls.map((call, index) =>
          index === existingIndex ? patchIncomingCall(call, nextCall) : call
        )
      : [nextCall, ...prev.incomingCalls].slice(0, 25);
  const queueableCalls = patchQueueableCallsWithIncoming(
    prev.queueableCalls,
    existingIndex >= 0 ? incomingCalls[existingIndex]! : nextCall
  );
  if (hasEstablishedOperatorSession(prev)) {
    return {
      ...prev,
      incomingCalls,
      queueableCalls,
    };
  }

  const latest = [...incomingCalls].sort(
    (a, b) => b.receivedAtMs - a.receivedAtMs
  )[0];

  // These fields describe whoever the operator is being shown, so falling back to `prev` is
  // only safe while that is still the same call. Socket updates for one call often arrive
  // with the caller fields empty, which is what the fallback is for; but when the newest call
  // is a different one that arrives unidentified, the same fallback puts the previous
  // caller's name and profile on screen. The operator then opens the wrong client's emergency
  // contacts and medical notes while a real alarm is ringing.
  const isSameCallAsPrev =
    latest != null &&
    ((latest.callUuid != null && latest.callUuid === prev.callUuid) ||
      (latest.callId != null && latest.callId === prev.callId));
  const carryOver = <T,>(
    next: T | null | undefined,
    previous: T | null | undefined
  ): T | null => next ?? (isSameCallAsPrev ? previous ?? null : null);

  return {
    ...prev,
    callStatus: "ringing",
    callDirection: "incoming",
    incomingCalls,
    queueableCalls,
    customerId: carryOver(latest?.customerId, prev.customerId),
    callUuid: latest?.callUuid ?? prev.callUuid,
    callId: latest?.callId ?? prev.callId,
    callReference: latest?.service ?? prev.callReference,
    unitId: carryOver(latest?.unitId, prev.unitId),
    callerNumber: carryOver(latest?.callerNumber, prev.callerNumber),
    customerName: carryOver(latest?.customerName, prev.customerName),
  };
}

function mapSnapshotAnsweredToActiveCall(entry: CallSnapshotAnsweredEntry) {
  const startedAtMs = entry.startedAt ? Date.parse(entry.startedAt) : NaN;
  const answeredAtMs = entry.answeredAt ? Date.parse(entry.answeredAt) : NaN;
  return {
    callUuid: entry.uniqueId,
    callId: entry.uniqueId,
    callerNumber: entry.caller ?? null,
    customerName: null,
    callReference: entry.callType ?? null,
    callStartedAt:
      Number.isFinite(answeredAtMs) ? answeredAtMs : Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
    deviceCid: entry.deviceCid ?? null,
  };
}

function mapSnapshotOperatorsToAgents(
  operators: CallSnapshotPayload["activeOperators"]
): AgentPresence[] {
  return operators.map((operator) => ({
    id: operator.extension,
    name: operator.name,
    status:
      operator.activeCalls >= operator.maxConcurrentCalls ? ("busy" as const) : ("online" as const),
    sipExtension: operator.extension,
    sipUri: null,
    profileImageUrl: null,
  }));
}

function mapSnapshotAnsweredToEntry(entry: CallSnapshotAnsweredEntry): AnsweredCallEntry {
  return {
    uniqueId: entry.uniqueId,
    caller: entry.caller ?? null,
    callType: entry.callType ?? null,
    deviceCid: entry.deviceCid ?? null,
    startedAt: entry.startedAt ?? null,
    answeredAt: entry.answeredAt ?? null,
    takenBy: entry.takenBy ?? null,
    isMine: entry.isMine,
    observers: entry.observers ?? [],
  };
}

/** Authoritative queue + active-call seed from call:snapshot after identify. */
export function applyCallSnapshot(prev: CallState, snapshot: CallSnapshotPayload): CallState {
  const ringingCalls = snapshot.ringing.map(mapSnapshotRingingToIncomingCall);
  const myAnswered = snapshot.answered.find((entry) => entry.isMine === true) ?? null;

  const answeredCalls = snapshot.answered.map(mapSnapshotAnsweredToEntry);

  const queueableCalls = snapshot.ringing.map(mapSnapshotRingingToQueueableCall);

  let next: CallState = {
    ...prev,
    incomingCalls: [],
    queueableCalls,
    answeredCalls,
    agents:
      snapshot.activeOperators.length > 0
        ? mapSnapshotOperatorsToAgents(snapshot.activeOperators)
        : prev.agents,
  };

  for (const call of ringingCalls) {
    next = applyIncomingCall(next, call);
  }

  if (myAnswered) {
    const activeMeta = mapSnapshotAnsweredToActiveCall(myAnswered);
    next = {
      ...next,
      callStatus: "active",
      callDirection: "incoming",
      callUuid: activeMeta.callUuid,
      callId: activeMeta.callId,
      callerNumber: activeMeta.callerNumber,
      customerName: activeMeta.customerName,
      callReference: activeMeta.callReference,
      callStartedAt: activeMeta.callStartedAt,
      incomingCalls: next.incomingCalls.filter(
        (call) => getIncomingCallIdentity(call) !== myAnswered.uniqueId
      ),
    };
    return next;
  }

  const hasIncoming = next.incomingCalls.length > 0;
  if (
    !hasIncoming &&
    next.callDirection === "incoming" &&
    next.callStatus === "ringing"
  ) {
    return {
      ...next,
      callStatus: "idle",
      callDirection: undefined,
    };
  }

  return next;
}

export function appendCallTakenNotice(
  prev: CallState,
  notice: Omit<CallTakenNotice, "id">
): CallState {
  const entry: CallTakenNotice = {
    ...notice,
    id: `${notice.atMs}-${notice.uniqueId}`,
  };
  return {
    ...prev,
    callTakenNotices: [entry, ...prev.callTakenNotices].slice(0, 5),
  };
}

export function dismissCallTakenNotice(prev: CallState, noticeId: string): CallState {
  const nextNotices = prev.callTakenNotices.filter((notice) => notice.id !== noticeId);
  if (nextNotices.length === prev.callTakenNotices.length) return prev;
  return {
    ...prev,
    callTakenNotices: nextNotices,
  };
}

/** Add a new answered-call entry when call:taken fires. */
export function addAnsweredCall(
  prev: CallState,
  entry: AnsweredCallEntry
): CallState {
  const exists = prev.answeredCalls.some((c) => c.uniqueId === entry.uniqueId);
  if (exists) return prev;

  return {
    ...prev,
    answeredCalls: [...prev.answeredCalls, entry],
  };
}

/** Apply call:taken — including barge supervisor promotion to call owner. */
export function getActiveCallCorrelator(
  state: Pick<CallState, "callUuid" | "callId">
) {
  return state.callUuid?.trim() || state.callId?.trim() || null;
}

export function isPendingBargeTransferForCall(
  state: Pick<CallState, "pendingBargeTransfer" | "callUuid" | "callId">,
  uniqueId?: string | null,
  nowMs = Date.now()
) {
  const pending = state.pendingBargeTransfer;
  if (!pending) return false;

  const correlator = uniqueId ?? getActiveCallCorrelator(state);
  if (!correlator || pending.uniqueId !== correlator) return false;

  return nowMs - pending.atMs <= PENDING_BARGE_TRANSFER_WINDOW_MS;
}

export function shouldSuppressCallResolutionForTransfer(
  state: Pick<
    CallState,
    | "pendingBargeTransfer"
    | "bargeTransferNotice"
    | "callUuid"
    | "callId"
  >,
  uniqueId?: string | null,
  nowMs = Date.now()
) {
  const pending = state.pendingBargeTransfer;
  if (
    pending &&
    nowMs - pending.atMs <= PENDING_BARGE_TRANSFER_WINDOW_MS &&
    (!uniqueId || pending.uniqueId === uniqueId)
  ) {
    return true;
  }

  if (!uniqueId) {
    return state.bargeTransferNotice != null;
  }

  return state.bargeTransferNotice?.uniqueId === uniqueId;
}

export function shouldSkipEndedCallHistoryForTransfer(
  state: Pick<
    CallState,
    | "pendingBargeTransfer"
    | "bargeTransferNotice"
    | "callUuid"
    | "callId"
  >,
  nowMs = Date.now()
) {
  return shouldSuppressCallResolutionForTransfer(state, null, nowMs);
}

const clearLiveCallSessionFields = (): Partial<CallState> => ({
  callStatus: "idle",
  callStartedAt: null,
  callElapsedSeconds: 0,
  isMuted: false,
  isOnHold: false,
  conferenceRoom: null,
  participants: [],
  activeSuperviseMode: null,
  bargePromotionNotice: null,
  backgroundHeldCallUuid: null,
  backgroundHeldCallReference: null,
  heldCallerNumber: null,
  consultNumber: null,
  consultStatus: null,
  callUuid: null,
  callId: null,
  callReference: null,
  callerNumber: null,
  lastError: null,
});

export function applyOperatorTransferredAway(
  prev: CallState,
  payload: CallTakenPayload
): CallState {
  const uniqueId = payload.uniqueId;
  const existingIndex = prev.answeredCalls.findIndex(
    (call) => call.uniqueId === uniqueId
  );

  let answeredCalls: AnsweredCallEntry[];

  if (existingIndex >= 0) {
    answeredCalls = prev.answeredCalls.map((call, index) => {
      if (index !== existingIndex) {
        return call.isMine && call.uniqueId === uniqueId
          ? { ...call, isMine: false }
          : call;
      }

      return {
        ...call,
        isMine: false,
        takenBy: payload.takenBy,
        answeredAt: payload.at,
        ...(payload.caller != null ? { caller: payload.caller } : {}),
        observers: call.observers.filter(
          (observer) => observer.extension !== payload.takenBy.extension
        ),
      };
    });
  } else {
    answeredCalls = [
      ...prev.answeredCalls.filter(
        (call) => !(call.isMine && call.uniqueId === uniqueId)
      ),
      {
        uniqueId,
        caller: payload.caller ?? null,
        callType: null,
        deviceCid: null,
        startedAt: null,
        answeredAt: payload.at,
        takenBy: payload.takenBy,
        isMine: false,
        observers: [],
      },
    ];
  }

  const onThisCall =
    prev.callUuid === uniqueId || prev.callId === uniqueId;

  let next: CallState = {
    ...prev,
    answeredCalls,
    pendingBargeTransfer: null,
    bargeTransferNotice: {
      uniqueId,
      transferredToName: payload.takenBy.name,
      transferredToExtension: payload.takenBy.extension,
      atMs: Date.now(),
    },
    lastError: null,
  };

  if (!onThisCall) {
    return next;
  }

  return {
    ...next,
    ...clearLiveCallSessionFields(),
  };
}

export function applySipCallEnded(prev: CallState): CallState {
  const transferInProgress =
    prev.bargeTransferNotice != null ||
    isPendingBargeTransferForCall(prev);

  if (transferInProgress) {
    return {
      ...prev,
      ...clearLiveCallSessionFields(),
      pendingBargeTransfer: prev.pendingBargeTransfer,
      bargeTransferNotice: prev.bargeTransferNotice,
    };
  }

  return {
    ...prev,
    callStatus: "ended",
    callStartedAt: null,
    callElapsedSeconds: 0,
    isMuted: false,
    isOnHold: false,
    conferenceRoom: null,
    participants: [],
    activeSuperviseMode: null,
    bargePromotionNotice: null,
    backgroundHeldCallUuid: null,
    backgroundHeldCallReference: null,
    heldCallerNumber: null,
    consultNumber: null,
    consultStatus: null,
  };
}

export function applyCallTaken(
  prev: CallState,
  payload: CallTakenPayload,
  resolvedExtension: string | null | undefined
): CallState {
  const isSelfOwner =
    !!resolvedExtension && payload.takenBy.extension === resolvedExtension;
  const isPromotion = Boolean(payload.promotedFrom?.trim());
  const promotedFrom = payload.promotedFrom?.trim() ?? null;
  const isTransferredAway =
    !!resolvedExtension &&
    isPromotion &&
    promotedFrom === resolvedExtension;

  if (isTransferredAway) {
    return applyOperatorTransferredAway(prev, payload);
  }

  const existingIndex = prev.answeredCalls.findIndex(
    (call) => call.uniqueId === payload.uniqueId
  );

  let answeredCalls: AnsweredCallEntry[];

  if (existingIndex >= 0) {
    const existing = prev.answeredCalls[existingIndex];
    answeredCalls = prev.answeredCalls.map((call, index) => {
      if (index !== existingIndex) return call;

      return {
        ...existing,
        takenBy: payload.takenBy,
        answeredAt: payload.at,
        ...(payload.caller != null ? { caller: payload.caller } : {}),
        ...(isSelfOwner ? { isMine: true } : {}),
        observers:
          isSelfOwner && isPromotion
            ? call.observers.filter(
                (observer) => observer.extension !== resolvedExtension
              )
            : call.observers,
      };
    });
  } else {
    answeredCalls = [
      ...prev.answeredCalls,
      {
        uniqueId: payload.uniqueId,
        caller: payload.caller ?? null,
        callType: null,
        deviceCid: null,
        startedAt: null,
        answeredAt: payload.at,
        takenBy: payload.takenBy,
        isMine: isSelfOwner,
        observers: [],
      },
    ];
  }

  let next: CallState = { ...prev, answeredCalls };

  const onPromotedCall =
    prev.callUuid === payload.uniqueId || prev.callId === payload.uniqueId;

  if (isSelfOwner && isPromotion && onPromotedCall) {
    next = {
      ...next,
      activeSuperviseMode: null,
      bargePromotionNotice: {
        uniqueId: payload.uniqueId,
        promotedFromExtension: promotedFrom!,
        atMs: Date.now(),
      },
    };
  }

  if (isSelfOwner) {
    return next;
  }

  const hadIncoming = prev.incomingCalls.some((call) =>
    matchesCallEndedUniqueId(call, payload.uniqueId)
  );
  if (!hadIncoming) {
    return next;
  }

  return appendCallTakenNotice(
    removeIncomingCallByUniqueId(next, payload.uniqueId),
    {
      uniqueId: payload.uniqueId,
      caller: payload.caller ?? null,
      takenByName: payload.takenBy.name,
      takenByExtension: payload.takenBy.extension,
      atMs: Date.parse(payload.at) || Date.now(),
    }
  );
}

/** Remove an answered-call entry when call:ended fires. */
export function removeAnsweredCall(
  prev: CallState,
  uniqueId: string
): CallState {
  const next = prev.answeredCalls.filter((c) => c.uniqueId !== uniqueId);
  if (next.length === prev.answeredCalls.length) return prev;

  const clearPromotion =
    prev.bargePromotionNotice?.uniqueId === uniqueId
      ? { bargePromotionNotice: null }
      : {};
  const clearTransfer =
    prev.bargeTransferNotice?.uniqueId === uniqueId
      ? { bargeTransferNotice: null }
      : {};
  const clearPending =
    prev.pendingBargeTransfer?.uniqueId === uniqueId
      ? { pendingBargeTransfer: null }
      : {};

  return {
    ...prev,
    answeredCalls: next,
    ...clearPromotion,
    ...clearTransfer,
    ...clearPending,
  };
}

/** When this operator was invited to supervise, bind the live SIP session to that call. */
export function applySelfInvitedSuperviseSession(
  prev: CallState,
  payload: ObserverJoinedPayload,
  myExtension: string
): CallState {
  if (payload.observer.extension !== myExtension) return prev;

  const call = prev.answeredCalls.find((entry) => entry.uniqueId === payload.uniqueId);

  return {
    ...prev,
    callUuid: payload.uniqueId,
    callerNumber: call?.caller ?? prev.callerNumber ?? null,
    callReference: call?.callType ?? prev.callReference ?? null,
    activeSuperviseMode: payload.observer.mode,
  };
}

/** Append an observer to a call's observers list. */
export function applyObserverJoined(
  prev: CallState,
  payload: ObserverJoinedPayload
): CallState {
  const callIndex = prev.answeredCalls.findIndex(
    (c) => c.uniqueId === payload.uniqueId
  );
  if (callIndex === -1) return prev;

  const call = prev.answeredCalls[callIndex];
  const alreadyPresent = call.observers.some(
    (o) => o.extension === payload.observer.extension
  );
  if (alreadyPresent) return prev;

  const updatedCall: AnsweredCallEntry = {
    ...call,
    observers: [...call.observers, payload.observer],
  };

  const answeredCalls = prev.answeredCalls.map((c, i) =>
    i === callIndex ? updatedCall : c
  );

  return { ...prev, answeredCalls };
}

/** Remove an observer from a call's observers list. */
export function applyObserverLeft(
  prev: CallState,
  payload: ObserverLeftPayload
): CallState {
  const callIndex = prev.answeredCalls.findIndex(
    (c) => c.uniqueId === payload.uniqueId
  );
  if (callIndex === -1) return prev;

  const call = prev.answeredCalls[callIndex];
  const nextObservers = call.observers.filter(
    (o) => o.extension !== payload.extension
  );
  if (nextObservers.length === call.observers.length) return prev;

  const updatedCall: AnsweredCallEntry = {
    ...call,
    observers: nextObservers,
  };

  const answeredCalls = prev.answeredCalls.map((c, i) =>
    i === callIndex ? updatedCall : c
  );

  return { ...prev, answeredCalls };
}

/** Single place for "add alarm signal" state update. */
export function applyAlarmSignal(
  prev: CallState,
  nextSignal: AlarmSignalInfo
): CallState {
  const nextIdentity = nextSignal.callUuid ?? nextSignal.id;
  const filtered = prev.alarmSignals.filter(
    (signal) => (signal.callUuid ?? signal.id) !== nextIdentity
  );
  const alarmSignals = [nextSignal, ...filtered]
    .sort((a, b) => b.receivedAtMs - a.receivedAtMs)
    .slice(0, 100);
  return {
    ...prev,
    alarmSignals,
  };
}

function getQueueableCallUniqueId(call: QueueableCall | IncomingCallInfo) {
  return (
    call.uniqueId?.trim() ||
    call.callUuid?.trim() ||
    call.callId?.trim() ||
    call.id?.trim() ||
    null
  );
}

/** Replace queueable calls from REST and prune stale claim state. */
export function applyQueueableCalls(
  prev: CallState,
  nextCalls: QueueableCall[]
): CallState {
  const nextUniqueIds = new Set(
    nextCalls
      .map((call) => getQueueableCallUniqueId(call))
      .filter((value): value is string => Boolean(value))
  );

  const queueClaimByUniqueId: Record<string, QueueClaimStatus> = {};
  for (const [uniqueId, status] of Object.entries(prev.queueClaimByUniqueId)) {
    if (!nextUniqueIds.has(uniqueId)) continue;
    if (status === "claiming" || status === "claimed-by-me" || status === "claimed-by-other") {
      queueClaimByUniqueId[uniqueId] = status;
    }
  }

  const prevIds = prev.queueableCalls
    .map((call) => getQueueableCallUniqueId(call))
    .join("|");
  const nextIds = nextCalls
    .map((call) => getQueueableCallUniqueId(call))
    .join("|");
  const claimsUnchanged =
    Object.keys(prev.queueClaimByUniqueId).length ===
      Object.keys(queueClaimByUniqueId).length &&
    Object.entries(queueClaimByUniqueId).every(
      ([uniqueId, status]) => prev.queueClaimByUniqueId[uniqueId] === status
    );
  const enrichmentUnchanged = prev.queueableCalls.every((prevCall, index) => {
    const nextCall = nextCalls[index];
    if (!nextCall) return false;
    return (
      (prevCall.customerId ?? null) === (nextCall.customerId ?? null) &&
      (prevCall.customerName ?? null) === (nextCall.customerName ?? null) &&
      (prevCall.unitId ?? null) === (nextCall.unitId ?? null)
    );
  });

  if (prevIds === nextIds && claimsUnchanged && enrichmentUnchanged) {
    return prev;
  }

  return {
    ...prev,
    queueableCalls: nextCalls,
    queueClaimByUniqueId,
  };
}

export function setQueueClaimStatus(
  prev: CallState,
  uniqueId: string,
  status: QueueClaimStatus
): CallState {
  const normalizedUniqueId = uniqueId.trim();
  if (!normalizedUniqueId) return prev;

  if (status === "available") {
    if (!prev.queueClaimByUniqueId[normalizedUniqueId]) return prev;
    const queueClaimByUniqueId = { ...prev.queueClaimByUniqueId };
    delete queueClaimByUniqueId[normalizedUniqueId];
    return { ...prev, queueClaimByUniqueId };
  }

  const current = prev.queueClaimByUniqueId[normalizedUniqueId];
  if (current === status) return prev;

  return {
    ...prev,
    queueClaimByUniqueId: {
      ...prev.queueClaimByUniqueId,
      [normalizedUniqueId]: status,
    },
  };
}

export function applyQueueClaimed(
  prev: CallState,
  uniqueId: string,
  extension: string,
  myExtension: string | null | undefined
): CallState {
  const normalizedUniqueId = uniqueId.trim();
  const normalizedExtension = extension.trim();
  if (!normalizedUniqueId || !normalizedExtension) return prev;

  const status: QueueClaimStatus =
    myExtension && normalizedExtension === myExtension
      ? "claimed-by-me"
      : "claimed-by-other";

  return setQueueClaimStatus(prev, normalizedUniqueId, status);
}

export function applyQueueClaimReleased(
  prev: CallState,
  uniqueId: string
): CallState {
  return setQueueClaimStatus(prev, uniqueId, "available");
}

export function readQueueClaimStatus(
  prev: CallState,
  uniqueId: string
): QueueClaimStatus {
  return prev.queueClaimByUniqueId[uniqueId.trim()] ?? "available";
}
